import {
  Typography,
  Button,
  Card,
  CardContent,
  Container,
  Grid,
  Box,
  Stack,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import locales from "../locales.json";
import { useLang } from "../context/LangContext";

/** Full-bleed wrapper for edge-to-edge styling */
function FullBleed({ children, sx }) {
  return (
    <Box
      sx={{
        position: "relative",
        left: "50%",
        right: "50%",
        marginLeft: "-50vw",
        marginRight: "-50vw",
        width: "100vw",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function LicensePage({ isAuthenticated }) {
  const navigate = useNavigate();
  const { lang } = useLang();

  const handlePurchase = (licenseType) => {
    const state = { licenseType };
    isAuthenticated
      ? navigate("/payment", { state })
      : navigate("/signin", { state: { from: "/payment", ...state } });
  };

  return (
    <Box>
      {/* Hero Section with Pricing Cards */}
      <FullBleed
        sx={{
          position: "relative",
          overflow: "hidden",
          py: { xs: 4, md: 6 },
          marginTop: { xs: -2, md: -2 },
          minHeight: "calc(80vh - 64px)",
          display: "flex",
          alignItems: "center",
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          background:
            "linear-gradient(135deg, rgba(59,130,246,.16), rgba(168,85,247,.16))",

          // LEFT photo
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-left.jpg')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "-60px center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },

          // RIGHT photo
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: "url('/doctor-right.webp')",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "right center",
            backgroundSize: { xs: "0 0", md: "auto 100%" },
            opacity: { md: 0.28, lg: 0.26 },
            filter: "grayscale(100%) contrast(.95) saturate(.85)",
            mixBlendMode: "normal",
            pointerEvents: "none",
          },
        }}
      >
        {/* Overlay SCRIM */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1,
            pointerEvents: "none",
            background:
              "linear-gradient(90deg,rgba(143, 143, 143, 0.01) 0%, #dadef8ff 50%, rgba(150, 150, 150, 0.01) 100%)",
            backgroundRepeat: "no-repeat, no-repeat",
            backgroundPosition: "center, center",
            backgroundSize: "100% 100%, 100% 100%",
            mixBlendMode: "normal",
          }}
        />

        {/* Hero content with pricing cards */}
        <Container
          maxWidth="xl"
          disableGutters
          sx={{ position: "relative", zIndex: 2 }}
        >
          <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
            <Stack spacing={4}>
              <Box sx={{ textAlign: "center" }}>
                <Typography
                  component="h1"
                  variant="h2"
                  sx={{
                    fontWeight: 700,
                    lineHeight: { xs: 1.2, md: 1.3 },
                    fontSize: {
                      xs: "1.8rem",
                      sm: "2.2rem",
                      md: "2.5rem",
                      lg: "2.8rem",
                    },
                    color: "text.primary",
                    maxWidth: 900,
                    mx: "auto",
                    "@supports (text-wrap: balance)": { textWrap: "balance" },
                  }}
                >
                  {locales.license_page_title?.[lang] ||
                    "Choisissez votre abonnement"}
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.1rem" },
                    letterSpacing: ".2px",
                    maxWidth: 700,
                    mx: "auto",
                    mt: 2,
                  }}
                >
                  {locales.license_page_subtitle?.[lang] ||
                    "Sélectionnez l'offre qui correspond à vos besoins"}
                </Typography>
              </Box>

              {/* Pricing Cards */}
              <Grid
                container
                justifyContent="center"
                alignItems="stretch"
                gap={2}
              >
                {/* Carte de gauche */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                  <Card
                    elevation={0}
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      minHeight: { xs: "auto", md: "480px" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {locales.free_version[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        {locales.free_version_desc[lang]}
                      </Typography>
                      <ul
                        style={{
                          marginTop: "16px",
                          paddingLeft: "20px",
                          lineHeight: 1.8,
                        }}
                      >
                        <li>{locales.free_feature_1[lang]}</li>
                        <li>{locales.free_feature_2[lang]}</li>
                      </ul>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        component={Link}
                        to="/download"
                        color="success"
                        fullWidth
                        sx={{
                          borderRadius: 999,
                          mt: 3,
                          py: 1.5,
                          fontSize: { xs: "1rem", md: "1rem" },
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {locales.free_button[lang]}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Carte du milieu */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                  <Card
                    elevation={0}
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      minHeight: { xs: "auto", md: "480px" },
                      position: "relative",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {locales.premium_license[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {locales.premium_price_label[lang]}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: "bold", color: "primary.main" }}
                      >
                        {locales.premium_price[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {locales.premium_price_unit[lang]}
                      </Typography>
                      <ul
                        style={{
                          marginTop: "16px",
                          paddingLeft: "20px",
                          lineHeight: 1.8,
                        }}
                      >
                        <li>{locales.premium_feature_1[lang]}</li>
                        <li>{locales.premium_feature_2[lang]}</li>
                        <li>{locales.premium_feature_3[lang]}</li>
                      </ul>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handlePurchase("premium")}
                        fullWidth
                        sx={{
                          borderRadius: 999,
                          mt: 3,
                          py: 1.5,
                          fontSize: { xs: "1rem", md: "1rem" },
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {locales.premium_button[lang]}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Carte de droite */}
                <Grid item xs={12} sm={6} md={4} sx={{ display: "flex" }}>
                  <Card
                    elevation={0}
                    sx={{
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: "divider",
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      minHeight: { xs: "auto", md: "480px" },
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: 3,
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        flexDirection: "column",
                        p: 3,
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        {locales.enterprise_license[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 0.5 }}
                      >
                        {locales.enterprise_price_label[lang]}
                      </Typography>
                      <Typography
                        variant="h3"
                        sx={{ fontWeight: "bold", color: "secondary.main" }}
                      >
                        {locales.enterprise_price[lang]}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        {locales.enterprise_price_unit[lang]}
                      </Typography>
                      <ul
                        style={{
                          marginTop: "16px",
                          paddingLeft: "20px",
                          lineHeight: 1.8,
                        }}
                      >
                        <li>{locales.enterprise_feature_1[lang]}</li>
                        <li>{locales.enterprise_feature_2[lang]}</li>
                        <li>{locales.enterprise_feature_3[lang]}</li>
                      </ul>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handlePurchase("enterprise")}
                        fullWidth
                        sx={{
                          borderRadius: 999,
                          mt: 3,
                          py: 1.5,
                          fontSize: { xs: "1rem", md: "1rem" },
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {locales.enterprise_button[lang]}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Stack>
          </Container>
        </Container>
      </FullBleed>
    </Box>
  );
}

export default LicensePage;
